type ExtendedQueryResult<QueryResult extends object, Extensions extends object> = QueryResult extends object
    ? Omit<QueryResult, keyof Extensions> & Extensions
    : never

const hasOwn = (object: object, property: PropertyKey): boolean =>
    Object.prototype.hasOwnProperty.call(object, property)

/**
 * Adds or replaces fields without eagerly reading every property from TanStack Query's tracked result proxy.
 * This avoids subscribing to unused query properties and triggering excessive re-renders. It also avoids violating
 * the `@tanstack/query/no-rest-destructuring` lint rule, which is important for performance.
 */
export function extendQueryResult<QueryResult extends object, Extensions extends object>(
    queryResult: QueryResult,
    extensions: Extensions
): ExtendedQueryResult<QueryResult, Extensions>
export function extendQueryResult(queryResult: object, extensions: object): object {
    return new Proxy(queryResult, {
        get(target, property, receiver) {
            if (hasOwn(extensions, property)) {
                return Reflect.get(extensions, property)
            }

            return Reflect.get(target, property, receiver)
        },
        getOwnPropertyDescriptor(target, property) {
            if (hasOwn(extensions, property)) {
                return Reflect.getOwnPropertyDescriptor(extensions, property)
            }

            return Reflect.getOwnPropertyDescriptor(target, property)
        },
        has(target, property) {
            return hasOwn(extensions, property) || Reflect.has(target, property)
        },
        ownKeys(target) {
            return [...new Set([...Reflect.ownKeys(target), ...Reflect.ownKeys(extensions)])]
        },
    })
}
